export const TDT_KEY = 'd727f2a95be0e2bb60fde6e8052e21f3';

const SCRIPT_ID = 'tianditu-sdk-script';
const SCRIPT_SRC = 'https://api.tianditu.gov.cn/api?v=4.0&tk=' + TDT_KEY;

let loadPromise: Promise<any> | null = null;
const overlayStateMap = new WeakMap<any, {
    root: HTMLDivElement;
    items: Set<any>;
    scheduleUpdate: () => void;
}>();

export function getT() {
    return (window as any).T;
}

export function ensureTianDiTuLoaded() {
    let T = getT();
    if (T && T.Map) {
        return Promise.resolve(T);
    }
    if (loadPromise) {
        return loadPromise;
    }
    loadPromise = new Promise((resolve, reject) => {
        let onReady = () => {
            let loadedT = getT();
            if (loadedT && loadedT.Map) {
                resolve(loadedT);
                return;
            }
            reject(new Error('TianDiTu SDK loaded but window.T is unavailable.'));
        };
        let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (!script) {
            script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = SCRIPT_SRC;
            script.type = 'text/javascript';
            script.async = true;
            script.onload = onReady;
            script.onerror = () => reject(new Error('Failed to load TianDiTu SDK.'));
            document.head.appendChild(script);
            return;
        }
        if ((script as any).dataset.loaded === 'true') {
            onReady();
            return;
        }
        script.addEventListener('load', onReady, { once: true });
        script.addEventListener('error', () => reject(new Error('Failed to load TianDiTu SDK.')), { once: true });
    }).then(TMap => {
        let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (script) {
            (script as any).dataset.loaded = 'true';
        }
        return TMap;
    }).catch(error => {
        loadPromise = null;
        throw error;
    });
    return loadPromise;
}

export function applyTdtTheme(containerId: string) {
    let container = document.getElementById(containerId);
    if (!container) {
        return;
    }
    if (container.className.indexOf('tianditu-map-host') === -1) {
        container.className = (container.className ? container.className + ' ' : '') + 'tianditu-map-host';
    }
}

export function applyOfficialMapStyle(map: any, style: 'black' | 'indigo' = 'indigo') {
    if (!map || typeof map.setStyle !== 'function') {
        return;
    }
    let run = () => {
        try {
            map.setStyle(style);
        } catch (error) {
        }
    };
    run();
    setTimeout(run, 120);
    setTimeout(run, 500);
    if (typeof map.addEventListener === 'function') {
        map.addEventListener('load', run);
    }
}

export function createLngLat(lng: number | string, lat: number | string) {
    let T = getT();
    return new T.LngLat(Number(lng), Number(lat));
}

export function createPixelPoint(x: number | string, y: number | string) {
    let T = getT();
    return new T.Point(Number(x), Number(y));
}

export function createLocationPoint(locationPoint: string) {
    let points = (locationPoint || '').split(',');
    if (points.length < 2) {
        return null;
    }
    return createLngLat(points[0], points[1]);
}

export function formatLngLat(lnglat: any) {
    let lng = getLng(lnglat);
    let lat = getLat(lnglat);
    if (lng === null || lat === null) {
        return '';
    }
    return Number(lng).toFixed(6) + ',' + Number(lat).toFixed(6);
}

export function getLng(lnglat: any) {
    if (!lnglat) {
        return null;
    }
    if (typeof lnglat.getLng === 'function') {
        return lnglat.getLng();
    }
    if (typeof lnglat.lng !== 'undefined') {
        return lnglat.lng;
    }
    if (typeof lnglat.lon !== 'undefined') {
        return lnglat.lon;
    }
    return null;
}

export function getLat(lnglat: any) {
    if (!lnglat) {
        return null;
    }
    if (typeof lnglat.getLat === 'function') {
        return lnglat.getLat();
    }
    if (typeof lnglat.lat !== 'undefined') {
        return lnglat.lat;
    }
    return null;
}

export function createMarkerIcon(iconUrl: string, width: number, height: number) {
    let T = getT();
    return new T.Icon({
        iconUrl: iconUrl,
        iconSize: new T.Point(width, height),
        iconAnchor: new T.Point(width / 2, height),
    });
}

function getMapContainer(map: any) {
    if (map && typeof map.getContainer === 'function') {
        return map.getContainer();
    }
    return null;
}

function getContainerPoint(map: any, lngLat: any) {
    if (!map || !lngLat) {
        return null;
    }
    try {
        if (typeof map.lngLatToContainerPoint === 'function') {
            return map.lngLatToContainerPoint(lngLat);
        }
        if (typeof map.lngLatToLayerPoint === 'function') {
            return map.lngLatToLayerPoint(lngLat);
        }
    } catch (error) {
        return null;
    }
    return null;
}

function ensureOverlayState(map: any) {
    let state = overlayStateMap.get(map);
    if (state) {
        return state;
    }
    let container = getMapContainer(map);
    if (!container) {
        throw new Error('TianDiTu map container is unavailable.');
    }
    if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
    }
    let root = document.createElement('div');
    root.className = 'tdt-html-overlay-root';
    root.style.position = 'absolute';
    root.style.left = '0';
    root.style.top = '0';
    root.style.right = '0';
    root.style.bottom = '0';
    root.style.pointerEvents = 'none';
    root.style.zIndex = '401';
    container.appendChild(root);

    let ticking = false;
    let scheduleUpdate = () => {
        if (ticking) {
            return;
        }
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            let currentState = overlayStateMap.get(map);
            if (!currentState) {
                return;
            }
            currentState.items.forEach((item: any) => item.update());
        });
    };

    ['move', 'moveend', 'zoom', 'zoomend', 'resize'].forEach(eventName => {
        if (typeof map.addEventListener === 'function') {
            map.addEventListener(eventName, scheduleUpdate);
        }
    });

    state = {
        root,
        items: new Set(),
        scheduleUpdate,
    };
    overlayStateMap.set(map, state);
    return state;
}

export function createHtmlOverlay(map: any, options: {
    lngLat: any;
    className?: string;
    html?: string;
    offsetX?: number;
    offsetY?: number;
    pointerEvents?: boolean;
}) {
    let state = ensureOverlayState(map);
    let element = document.createElement('div');
    element.className = options.className || '';
    element.style.position = 'absolute';
    element.style.left = '0';
    element.style.top = '0';
    element.style.transform = 'translate(-99999px, -99999px)';
    element.style.pointerEvents = options.pointerEvents ? 'auto' : 'none';
    if (typeof options.html !== 'undefined') {
        element.innerHTML = options.html;
    }
    state.root.appendChild(element);

    let overlay = {
        element,
        lngLat: options.lngLat,
        offsetX: options.offsetX || 0,
        offsetY: options.offsetY || 0,
        update() {
            let point = getContainerPoint(map, overlay.lngLat);
            let x = point && typeof point.x !== 'undefined' ? point.x : getLng(point);
            let y = point && typeof point.y !== 'undefined' ? point.y : getLat(point);
            if (x === null || y === null || typeof x === 'undefined' || typeof y === 'undefined') {
                return;
            }
            element.style.transform = 'translate(' + (Number(x) + overlay.offsetX) + 'px, ' + (Number(y) + overlay.offsetY) + 'px)';
        },
        setLngLat(lngLat: any) {
            overlay.lngLat = lngLat;
            overlay.update();
        },
        setHtml(html: string) {
            element.innerHTML = html;
        },
        remove() {
            state.items.delete(overlay);
            if (element.parentNode) {
                element.parentNode.removeChild(element);
            }
        },
    };

    state.items.add(overlay);
    state.scheduleUpdate();
    return overlay;
}
